import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import {
  getOptInTransactions,
  OPT_IN_TRANSACTION_STATUS_LABELS,
  OptInTransaction,
  OptInTransactionStatusFilter,
  OptInTransactionsPage,
} from "../../api/optInOffers";
import { formatCurrency, formatInteger } from "../../helpers/formatNumber";
import {
  dateToCopenhagenEndOfDayUTC,
  dateToCopenhagenStartOfDayUTC,
} from "../../helpers/dateFilters";
import { OptInTransactionsTable } from "./OptInTransactionsTable";

const PAGE_SIZE = 50;
const BADGE = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";

const EMPTY_COUNTS: OptInTransactionsPage["counts"] = {
  all: 0,
  paid: 0,
  compensated: 0,
  notOptedIn: 0,
  otherFailure: 0,
  manualCredit: 0,
};

interface OptInPersonModalProps {
  businessId: string;
  partnerName: string;
  identityId: string;
  onClose: () => void;
}

const Tile: React.FC<{ id: string; label: string; value: string }> = ({ id, label, value }) => (
  <div data-testid={`tile-${id}`} className="border border-gray-200 rounded-md px-4 py-2">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-medium">{value}</div>
  </div>
);

export const OptInPersonModal: React.FC<OptInPersonModalProps> = ({
  businessId,
  partnerName,
  identityId,
  onClose,
}) => {
  const [status, setStatus] = useState<OptInTransactionStatusFilter>("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [rows, setRows] = useState<OptInTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState<OptInTransactionsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response = await getOptInTransactions(businessId, {
          status,
          identityId,
          from: fromDate ? dateToCopenhagenStartOfDayUTC(fromDate) : undefined,
          to: toDate ? dateToCopenhagenEndOfDayUTC(toDate) : undefined,
          pageNumber,
          pageSize: PAGE_SIZE,
        });
        if (ignore) return;
        setRows(response.data);
        setTotal(response.total);
        setPage(response);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch transactions");
        console.error("Error fetching opt-in transactions:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [businessId, identityId, status, fromDate, toDate, pageNumber]);

  // Every response carries the person, so the header survives filters that return no rows.
  const person = page?.person ?? null;
  const counts = page?.counts ?? EMPTY_COUNTS;
  const rides = counts.paid + counts.compensated + counts.notOptedIn + counts.otherFailure;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const chips: { value: OptInTransactionStatusFilter; label: string; count: number }[] = [
    { value: "All", label: "All", count: counts.all },
    { value: "Paid", label: OPT_IN_TRANSACTION_STATUS_LABELS.Paid, count: counts.paid },
    { value: "Compensated", label: OPT_IN_TRANSACTION_STATUS_LABELS.Compensated, count: counts.compensated },
    { value: "NotOptedIn", label: OPT_IN_TRANSACTION_STATUS_LABELS.NotOptedIn, count: counts.notOptedIn },
    { value: "OtherFailure", label: OPT_IN_TRANSACTION_STATUS_LABELS.OtherFailure, count: counts.otherFailure },
    { value: "ManualCredit", label: OPT_IN_TRANSACTION_STATUS_LABELS.ManualCredit, count: counts.manualCredit },
  ];

  const changeStatus = (value: OptInTransactionStatusFilter) => {
    setStatus(value);
    setPageNumber(1);
  };

  return (
    <Modal
      className="modal-content md:max-w-[1000px]"
      overlayClassName="modal-overlay"
      isOpen
      onRequestClose={onClose}
      contentLabel={`${partnerName} transactions`}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="text-xs text-gray-500">{partnerName}</div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-primary font-medium text-xl">
              {person ? person.name ?? "Unknown" : ""}
            </h2>
            {person?.optInStatus === "OptedIn" && (
              <span className={`${BADGE} bg-green-100 text-green-800`}>Opted in</span>
            )}
            {person?.optInStatus === "OptedOut" && (
              <span className={`${BADGE} bg-gray-100 text-gray-800`}>Opted out</span>
            )}
            {person?.isDeleted && (
              <span className={`${BADGE} bg-gray-100 text-gray-800`}>Deleted account</span>
            )}
          </div>
          {person?.email && <div className="text-gray-600">{person.email}</div>}
        </div>
        <Button onClick={onClose} variant="icon" aria-label="Close">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Tile id="rides" label="Rides" value={formatInteger(rides)} />
        <Tile
          id="paid"
          label="Paid"
          value={`${formatInteger(counts.paid)} · ${formatCurrency(page?.paidCashback ?? 0)} kr`}
        />
        <Tile
          id="compensated"
          label="Compensated"
          value={`${formatInteger(counts.compensated)} · ${formatCurrency(
            page?.compensatedCashback ?? 0
          )} kr`}
        />
        <Tile id="not-compensated" label="Not compensated" value={formatInteger(counts.notOptedIn)} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {chips.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => changeStatus(chip.value)}
            className={`px-3 py-1 rounded-full text-sm border ${
              status === chip.value
                ? "bg-purple-500 text-white border-purple-500"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            {chip.label} ({formatInteger(chip.count)})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div>
          <label htmlFor="opt-in-person-from" className="block text-sm font-medium text-gray-700 mb-1">
            From
          </label>
          <input
            id="opt-in-person-from"
            type="date"
            className="p-2 border border-gray-300 rounded-md"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPageNumber(1);
            }}
          />
        </div>
        <div>
          <label htmlFor="opt-in-person-to" className="block text-sm font-medium text-gray-700 mb-1">
            To
          </label>
          <input
            id="opt-in-person-to"
            type="date"
            className="p-2 border border-gray-300 rounded-md"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPageNumber(1);
            }}
          />
        </div>
      </div>

      {error && <div className="error mb-4">{error}</div>}
      {loading && (
        <div className="flex justify-center my-4">
          <Loader />
        </div>
      )}

      <OptInTransactionsTable
        rows={rows}
        showPerson={false}
        emptyMessage={
          loading ? "Loading…" : status !== "All" || fromDate || toDate
            ? "No transactions match this filter."
            : "No transactions."
        }
      />

      <div className="flex justify-between items-center mt-4">
        <Button
          variant="outline"
          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          disabled={pageNumber === 1}
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages} · {formatInteger(total)} transactions
        </span>
        <Button
          variant="outline"
          onClick={() => setPageNumber((p) => p + 1)}
          disabled={pageNumber >= totalPages}
        >
          Next
        </Button>
      </div>
    </Modal>
  );
};
